import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { getRelativeTime } from '@pickleballinc/ui/components/global/utils/getRelativeTime';
import clsx from 'clsx';

import styles from '@/modules/homepage/SmallNewsCard/SmallNewsCard.module.css';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

export interface SmallNewsCardProps {
  description?: string;
  externalNews?: boolean;
  image?: string;
  imageAltText?: string;
  publishDateDisplayed?: string | Date;
  time?: string;
  title: string;
  author?: string;
  forNewsSearch?: boolean;
  url?: string;
  /** `compact` keeps the mobile layout at all breakpoints — used by the players dashboard widgets. */
  variant?: 'default' | 'compact';
}

export const SmallNewsCard = ({
  description,
  image,
  imageAltText,
  publishDateDisplayed,
  title,
  author,
  forNewsSearch,
  url,
  variant = 'default',
}: SmallNewsCardProps) => {
  const isCompact = variant === 'compact';
  const wrapperClasses = clsx(styles.wrapper);
  const imageClasses = clsx(
    styles.image,
    !isCompact && styles['image--responsive'],
    !isCompact && forNewsSearch && styles['image--for-news-search']
  );
  const contentClasses = clsx(styles.content, !isCompact && styles['content--responsive']);
  const titleClasses = clsx(styles.title, !isCompact && styles['title--responsive']);
  const descriptionClasses = clsx(styles.description);
  const iconTimeClasses = clsx(styles.icon__time);
  const iconClasses = clsx(styles.icon);
  const timeClasses = clsx(styles.time);

  const formattedPublishDate =
    publishDateDisplayed instanceof Date
      ? publishDateDisplayed
      : publishDateDisplayed
        ? new Date(publishDateDisplayed)
        : null;

  const relativeTime = formattedPublishDate ? getRelativeTime(formattedPublishDate) : '';

  return (
    <div className={wrapperClasses} itemScope itemType='https://schema.org/NewsArticle'>
      {url ? <meta itemProp='url' content={url} /> : null}
      {formattedPublishDate ? (
        <meta itemProp='datePublished' content={formattedPublishDate.toISOString()} />
      ) : null}
      <img
        className={imageClasses}
        style={{ objectFit: 'cover' }}
        alt={imageAltText || title}
        src={image || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 200, 200)}`}
        itemProp='thumbnail'
      />
      <div className={contentClasses}>
        <h3 className={titleClasses} itemProp='headline'>
          {title}
        </h3>
        {description ? (
          <p className={descriptionClasses} itemProp='accessibilitySummary'>
            {description}
          </p>
        ) : null}
        {relativeTime ? (
          <div className={iconTimeClasses}>
            <FontAwesomeIcon icon={faClock} size='sm' className={iconClasses} />
            <p className={timeClasses}>{relativeTime}</p>
            {author ? (
              <>
                <span>{` - `}</span>
                <p
                  className={timeClasses}
                  itemScope
                  itemProp='author'
                  itemType='https://schema.org/Person'
                >
                  <span itemProp='name'>{author}</span>
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
